import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaPlus,
  FaPlay,
  FaSpinner,
  FaDatabase,
  FaCheck,
  FaExclamationTriangle,
} from 'react-icons/fa';
import api from '../../../services/api';
import { highlightSQL } from './syntaxHighlighting';
import CreateSharedQueryModal from './CreateSharedQueryModal';
import ThemedSelect from '../../common/ThemedSelect';

const QuerySelectorWithEditor = ({ selectedQueryId, onQuerySelect, onQueryData }) => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const highlightRef = useRef(null);

  useEffect(() => {
    loadQueries();
  }, []);

  useEffect(() => {
    if (selectedQueryId && queries.length > 0) {
      loadQueryDetails(selectedQueryId);
    }
  }, [selectedQueryId, queries.length]);

  const loadQueries = async () => {
    try {
      setLoading(true);
      const res = await api.sharedQueries.getAll({ limit: 100 });
      const queryList = res.data?.data?.queries || res.data?.queries || res.data || [];
      setQueries(queryList);
    } catch (err) {
      console.error('Error loading queries:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadQueryDetails = async (queryId) => {
    try {
      const res = await api.sharedQueries.get(queryId);
      const query = res.data?.data || res.data;
      setSelectedQuery(query);
      if (onQueryData) onQueryData(query);
    } catch (err) {
      console.error('Error loading query details:', err);
    }
  };

  const handleQueryChange = (e) => {
    const queryId = parseInt(e.target.value);
    if (queryId) {
      onQuerySelect(queryId);
      loadQueryDetails(queryId);
    } else {
      onQuerySelect(null);
      setSelectedQuery(null);
      setTestResults(null);
    }
  };

  const handleTestExecute = async () => {
    if (!selectedQueryId) return;
    try {
      setExecuting(true);
      setTestResults(null);
      const res = await api.sharedQueries.execute(selectedQueryId);
      const data = res.data?.data || res.data;
      const rows = data?.rows || data?.results || [];
      setTestResults({
        success: true,
        rowCount: data?.rowCount || rows.length || 0,
        columns: rows.length > 0 ? Object.keys(rows[0]) : [],
        preview: rows.slice(0, 5),
      });
    } catch (err) {
      setTestResults({
        success: false,
        error: err.response?.data?.error || 'Query execution failed',
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleQueryCreated = (newQuery) => {
    const query = newQuery?.data || newQuery;
    setQueries((prev) => [query, ...prev]);
    const qId = query.query_id || query.QueryID || query.queryId || query.id;
    if (qId) {
      onQuerySelect(qId);
      setSelectedQuery(query);
    }
    setShowCreateModal(false);
  };

  const filteredQueries = queries.filter(
    (q) =>
      !searchTerm ||
      (q.query_name || q.QueryName || q.queryName || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Query Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Query *</label>
        <div className="flex items-center space-x-2">
          <ThemedSelect
            value={selectedQueryId || ''}
            onChange={handleQueryChange}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Select a query --</option>
            {filteredQueries.map((q) => (
              <option key={q.query_id || q.QueryID || q.id} value={q.query_id || q.QueryID || q.id}>
                {q.query_name || q.QueryName || q.queryName}{' '}
                {q.description ? `- ${q.description.substring(0, 50)}` : ''}
              </option>
            ))}
          </ThemedSelect>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center space-x-1.5 whitespace-nowrap"
          >
            <FaPlus className="w-3 h-3" />
            <span>New Query</span>
          </button>
        </div>
      </div>

      {/* SQL Preview */}
      {selectedQuery && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FaDatabase className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">
                {selectedQuery.query_name || selectedQuery.QueryName || selectedQuery.queryName}
              </span>
            </div>
            <button
              onClick={handleTestExecute}
              disabled={executing}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-primary-50 hover:bg-primary-100 transition-colors"
            >
              {executing ? (
                <FaSpinner className="animate-spin w-3 h-3" />
              ) : (
                <FaPlay className="w-3 h-3" />
              )}
              <span>{executing ? 'Running...' : 'Test Query'}</span>
            </button>
          </div>

          <div className="rounded-lg overflow-hidden border border-gray-700/50 bg-[#282c34]">
            <div className="px-3 py-1.5 bg-[#21252b] border-b border-gray-700/50">
              <span className="text-[11px] font-medium text-gray-400">SQL (read-only)</span>
            </div>
            <div className="relative" style={{ height: '180px' }}>
              <div className="absolute left-0 top-0 bottom-0 w-10 bg-[#282c34] border-r border-gray-700/30 overflow-hidden pointer-events-none z-10">
                <div className="pt-3 pb-3">
                  {Array.from(
                    {
                      length: (
                        selectedQuery.query_text ||
                        selectedQuery.QueryText ||
                        selectedQuery.SQLQuery ||
                        selectedQuery.sqlQuery ||
                        ''
                      ).split('\n').length,
                    },
                    (_, i) => (
                      <div
                        key={i}
                        className="text-right pr-2 text-[11px] leading-5 text-gray-600 select-none font-mono"
                      >
                        {i + 1}
                      </div>
                    )
                  )}
                </div>
              </div>
              <pre
                ref={highlightRef}
                className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 overflow-auto font-mono text-[13px] leading-5 text-[#abb2bf] whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={{
                  __html:
                    highlightSQL(
                      selectedQuery.query_text ||
                        selectedQuery.QueryText ||
                        selectedQuery.SQLQuery ||
                        selectedQuery.sqlQuery ||
                        ''
                    ) + '\n',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Test Results */}
      {testResults && (
        <div
          className={`rounded-lg border p-4 ${
            testResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}
        >
          {testResults.success ? (
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <FaCheck className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">
                  Query executed successfully - {testResults.rowCount} row
                  {testResults.rowCount !== 1 ? 's' : ''} returned
                </span>
              </div>
              {testResults.columns.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-green-600 mb-1">
                    Columns: {testResults.columns.join(', ')}
                  </p>
                  {testResults.preview.length > 0 && (
                    <div className="overflow-x-auto mt-2">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="bg-green-100/50">
                            {testResults.columns.slice(0, 6).map((col) => (
                              <th
                                key={col}
                                className="px-2 py-1 text-left font-medium text-green-700 border-b border-green-200"
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {testResults.preview.map((row, i) => (
                            <tr key={i} className="border-b border-green-100">
                              {testResults.columns.slice(0, 6).map((col) => (
                                <td
                                  key={col}
                                  className="px-2 py-1 text-green-800 max-w-[150px] truncate"
                                >
                                  {row[col] != null ? (
                                    String(row[col])
                                  ) : (
                                    <span className="text-green-400 italic">null</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {testResults.rowCount > 5 && (
                        <p className="text-[10px] text-green-500 mt-1">
                          Showing first 5 of {testResults.rowCount} rows
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <FaExclamationTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{testResults.error}</span>
            </div>
          )}
        </div>
      )}

      {/* Create Query Modal */}
      <CreateSharedQueryModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleQueryCreated}
      />
    </div>
  );
};

export default QuerySelectorWithEditor;
